import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { DollarSign, Database, Zap, Receipt, TrendingUp, HardDrive } from "lucide-react";

interface PlatformBillingSettings {
  id: string;
  aiUpchargeMultiplier: string;
  storageUpchargeMultiplier: string;
  aiCostPer1kTokens: string;
  storageGbMonthlyCost: string;
  updatedAt: string;
}

interface TenantAiUsageSummary {
  tenantId: string;
  tenantName: string;
  planType: string;
  tokensUsed: number;
  rawAiCostUsd: number;
  upchargedAiCostUsd: number;
  stripeCustomerId: string | null;
}

interface UsageSummaryResponse {
  rows: TenantAiUsageSummary[];
  settings: PlatformBillingSettings;
  month: string;
}

interface StorageStats {
  totalDbSizeBytes: number;
  totalDbSizeGb: number;
  rawStorageCostUsd: number;
  upchargedStorageCostUsd: number;
  tenantRowCounts: Array<{
    tenantId: string;
    tenantName: string;
    estimatedRows: number;
    estimatedSharePct: number;
    estimatedStorageGb: number;
    upchargedCostUsd: number;
  }>;
}

interface BillMonthResult {
  tenantId: string;
  tenantName: string;
  aiAmountCents: number;
  storageAmountCents: number;
  totalAmountCents: number;
  stripeInvoiceItemId: string | null;
  status: "billed" | "skipped_no_customer" | "skipped_zero" | "error";
  error?: string;
}

function formatUsd(amount: number) {
  return `$${amount.toFixed(4)}`;
}

function formatTokens(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function PlatformBillingSettingsPage() {
  const { toast } = useToast();
  const {
    isPlatformOwner,
    hasPermission,
    isLoading: permissionsLoading,
  } = usePlatformPermissions();
  const canViewBilling = isPlatformOwner || hasPermission("billing.view", "billing.manage");
  const canManageBilling = isPlatformOwner || hasPermission("billing.manage");
  const queryClient = useQueryClient();
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [billResults, setBillResults] = useState<BillMonthResult[] | null>(null);

  const settingsQuery = useQuery<PlatformBillingSettings>({
    queryKey: ["/api/admin/platform-billing/settings"],
    enabled: canViewBilling,
  });

  const usageQuery = useQuery<UsageSummaryResponse>({
    queryKey: ["/api/admin/platform-billing/usage-summary", selectedMonth],
    queryFn: () =>
      apiRequest("GET", `/api/admin/platform-billing/usage-summary?month=${selectedMonth}`).then(
        (r) => r.json()
      ),
    enabled: canViewBilling,
  });

  const storageQuery = useQuery<StorageStats>({
    queryKey: ["/api/admin/platform-billing/storage-stats"],
    enabled: canViewBilling,
  });

  const [aiMultiplier, setAiMultiplier] = useState("");
  const [storageMultiplier, setStorageMultiplier] = useState("");
  const [aiCostPer1k, setAiCostPer1k] = useState("");
  const [storageGbCost, setStorageGbCost] = useState("");

  const settings = settingsQuery.data;
  const effectiveAi = aiMultiplier || settings?.aiUpchargeMultiplier || "1.50";
  const effectiveStorage = storageMultiplier || settings?.storageUpchargeMultiplier || "1.50";

  const updateMutation = useMutation({
    mutationFn: async (patch: Partial<PlatformBillingSettings>) => {
      const r = await apiRequest("PATCH", "/api/admin/platform-billing/settings", patch);
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/platform-billing/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/platform-billing/usage-summary"] });
      setAiMultiplier("");
      setStorageMultiplier("");
      setAiCostPer1k("");
      setStorageGbCost("");
      toast({ title: "Settings saved" });
    },
    onError: () => toast({ title: "Failed to save settings", variant: "destructive" }),
  });

  const billMutation = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", "/api/admin/platform-billing/bill-month", {
        month: selectedMonth,
      });
      return r.json() as Promise<BillMonthResult[]>;
    },
    onSuccess: (results) => {
      setBillResults(results);
      const billed = results.filter((r) => r.status === "billed").length;
      toast({ title: `Billed ${billed} tenant(s) for ${selectedMonth}` });
    },
    onError: () => toast({ title: "Billing run failed", variant: "destructive" }),
  });

  function handleSave() {
    const patch: Record<string, string> = {};
    if (aiMultiplier) patch.aiUpchargeMultiplier = aiMultiplier;
    if (storageMultiplier) patch.storageUpchargeMultiplier = storageMultiplier;
    if (aiCostPer1k) patch.aiCostPer1kTokens = aiCostPer1k;
    if (storageGbCost) patch.storageGbMonthlyCost = storageGbCost;
    if (Object.keys(patch).length === 0) return;
    updateMutation.mutate(patch);
  }

  const storage = storageQuery.data;
  const usage = usageQuery.data;

  const totalUpchargedAi = usage?.rows.reduce((s, r) => s + r.upchargedAiCostUsd, 0) ?? 0;
  const totalUpchargedStorage = storage?.upchargedStorageCostUsd ?? 0;

  if (permissionsLoading) {
    return <div className="p-6 text-muted-foreground">Loading permissions…</div>;
  }
  if (!canViewBilling) {
    return <div className="p-6"><Card className="p-6">Access denied. Billing access is required.</Card></div>;
  }

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Platform Billing Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure how AI and storage costs are upcharged to subscribers
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Zap className="w-4 h-4" /> AI Upcharge
            </div>
            <p className="text-3xl font-bold">{parseFloat(effectiveAi).toFixed(2)}×</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <HardDrive className="w-4 h-4" /> Storage Upcharge
            </div>
            <p className="text-3xl font-bold">{parseFloat(effectiveStorage).toFixed(2)}×</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <TrendingUp className="w-4 h-4" /> AI Revenue ({selectedMonth})
            </div>
            {usageQuery.isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p className="text-3xl font-bold">${totalUpchargedAi.toFixed(2)}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Database className="w-4 h-4" /> Storage Revenue (est.)
            </div>
            {storageQuery.isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p className="text-3xl font-bold">${totalUpchargedStorage.toFixed(2)}/mo</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Multiplier settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" /> Upcharge Multipliers
          </CardTitle>
          <CardDescription>
            Adjust what factor is applied on top of your raw provider costs before billing subscribers.
            Default is 1.5× (50% margin).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {settingsQuery.isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>AI Usage Multiplier</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="1"
                  max="10"
                  placeholder={settings?.aiUpchargeMultiplier ?? "1.50"}
                  value={aiMultiplier}
                  onChange={(e) => setAiMultiplier(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Current: {settings?.aiUpchargeMultiplier ?? "1.50"}× — our cost
                  ${settings?.aiCostPer1kTokens ?? "0.010"}/1K tokens →
                  tenants pay ${(
                    parseFloat(settings?.aiCostPer1kTokens ?? "0.010") *
                    parseFloat(effectiveAi)
                  ).toFixed(4)}/1K tokens
                </p>
              </div>
              <div className="space-y-2">
                <Label>Storage Multiplier</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="1"
                  max="10"
                  placeholder={settings?.storageUpchargeMultiplier ?? "1.50"}
                  value={storageMultiplier}
                  onChange={(e) => setStorageMultiplier(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Current: {settings?.storageUpchargeMultiplier ?? "1.50"}× — our cost
                  ${settings?.storageGbMonthlyCost ?? "0.20"}/GB/mo →
                  tenants pay ${(
                    parseFloat(settings?.storageGbMonthlyCost ?? "0.20") *
                    parseFloat(effectiveStorage)
                  ).toFixed(4)}/GB/mo
                </p>
              </div>
              <div className="space-y-2">
                <Label>Our AI Cost ($/1K tokens)</Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder={settings?.aiCostPer1kTokens ?? "0.010"}
                  value={aiCostPer1k}
                  onChange={(e) => setAiCostPer1k(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Blended average you pay Anthropic/OpenAI per 1,000 tokens
                </p>
              </div>
              <div className="space-y-2">
                <Label>Our Storage Cost ($/GB/month)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={settings?.storageGbMonthlyCost ?? "0.20"}
                  value={storageGbCost}
                  onChange={(e) => setStorageGbCost(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  What your database host charges you per GB per month
                </p>
              </div>
            </div>
          )}
          <Button
            onClick={handleSave}
            disabled={
              !canManageBilling ||
              updateMutation.isPending ||
              (!aiMultiplier && !storageMultiplier && !aiCostPer1k && !storageGbCost)
            }
          >
            {updateMutation.isPending ? "Saving…" : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      {/* Creating Stripe invoice items remains platform-owner-only. */}
      {isPlatformOwner && <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" /> Bill Tenants
          </CardTitle>
          <CardDescription>
            Creates Stripe invoice items for AI and storage usage. Run once per billing period.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="space-y-1">
              <Label>Billing Month</Label>
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setBillResults(null);
                }}
                className="w-44"
              />
            </div>
            <div className="pt-6">
              <Button
                onClick={() => billMutation.mutate()}
                disabled={billMutation.isPending}
                variant="default"
              >
                {billMutation.isPending ? "Billing…" : `Bill All Tenants — ${selectedMonth}`}
              </Button>
            </div>
          </div>

          {billResults && (
            <div className="mt-4 space-y-2">
              {billResults.map((r) => (
                <div
                  key={r.tenantId}
                  className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm"
                >
                  <span className="font-medium">{r.tenantName}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">
                      AI ${(r.aiAmountCents / 100).toFixed(2)} + Storage ${(r.storageAmountCents / 100).toFixed(2)}
                    </span>
                    <Badge
                      variant={
                        r.status === "billed"
                          ? "default"
                          : r.status === "error"
                          ? "destructive"
                          : "secondary"
                      }
                    >
                      {r.status === "billed"
                        ? `Billed $${(r.totalAmountCents / 100).toFixed(2)}`
                        : r.status === "skipped_no_customer"
                        ? "No Stripe customer"
                        : r.status === "skipped_zero"
                        ? "Zero usage"
                        : r.error ?? "Error"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>}

      {/* AI usage table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" /> AI Usage — {selectedMonth}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {usageQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : !usage?.rows.length ? (
            <p className="text-muted-foreground text-sm">No AI usage recorded for {selectedMonth}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Tenant</th>
                    <th className="pb-2 pr-4">Plan</th>
                    <th className="pb-2 pr-4 text-right">Tokens</th>
                    <th className="pb-2 pr-4 text-right">Raw Cost</th>
                    <th className="pb-2 text-right">Upcharged ({parseFloat(effectiveAi).toFixed(1)}×)</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.rows.map((row) => (
                    <tr key={row.tenantId} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{row.tenantName}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{row.planType}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">
                        {formatTokens(row.tokensUsed)}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-muted-foreground">
                        {formatUsd(row.rawAiCostUsd)}
                      </td>
                      <td className="py-2 text-right font-mono font-semibold">
                        {formatUsd(row.upchargedAiCostUsd)}
                      </td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td colSpan={4} className="pt-3 pr-4 text-right text-muted-foreground">
                      Total
                    </td>
                    <td className="pt-3 text-right font-mono">
                      {formatUsd(totalUpchargedAi)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Storage table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" /> Database Storage (estimated)
          </CardTitle>
          <CardDescription>
            Total DB: {storage?.totalDbSizeGb ?? "…"} GB — raw cost ${storage?.rawStorageCostUsd?.toFixed(2) ?? "…"}/mo —
            upcharged ${storage?.upchargedStorageCostUsd?.toFixed(2) ?? "…"}/mo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {storageQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : !storage?.tenantRowCounts.length ? (
            <p className="text-muted-foreground text-sm">No storage data available.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Tenant</th>
                    <th className="pb-2 pr-4 text-right">Est. Rows</th>
                    <th className="pb-2 pr-4 text-right">Share</th>
                    <th className="pb-2 pr-4 text-right">Est. GB</th>
                    <th className="pb-2 text-right">Upcharged/mo ({parseFloat(effectiveStorage).toFixed(1)}×)</th>
                  </tr>
                </thead>
                <tbody>
                  {storage.tenantRowCounts.map((row) => (
                    <tr key={row.tenantId} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{row.tenantName}</td>
                      <td className="py-2 pr-4 text-right font-mono">
                        {row.estimatedRows.toLocaleString()}
                      </td>
                      <td className="py-2 pr-4 text-right text-muted-foreground">
                        {row.estimatedSharePct}%
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">
                        {row.estimatedStorageGb.toFixed(3)}
                      </td>
                      <td className="py-2 text-right font-mono font-semibold">
                        ${row.upchargedCostUsd.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
